import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Modal, Form, InputNumber, Input, Select, Switch } from 'antd';
import { ArrowLeftOutlined, EditOutlined, ThunderboltOutlined, ReloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

// Hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

const { Title } = Typography;
const { confirm } = Modal;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    @media (max-width: 768px) {
      height: auto;
      min-height: 40px;
      padding: 8px 12px;
      font-size: 14px;
    }
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
  
  .ant-space {
    @media (max-width: 768px) {
      width: 100%;
      flex-wrap: wrap;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    
    @media (max-width: 768px) {
      padding: 12px 16px;
    }
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
    
    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
    padding: 16px;
    
    @media (max-width: 768px) {
      padding: 12px;
    }
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 12px;
      font-size: 13px;
      width: 40% !important;
    }
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
    
    @media (max-width: 768px) {
      padding: 8px 12px;
      font-size: 13px;
      word-break: break-word;
    }
  }
  
  .ant-descriptions {
    @media (max-width: 768px) {
      .ant-descriptions-row {
        display: flex;
        flex-direction: column;
        
        .ant-descriptions-item {
          display: flex;
          flex-direction: row;
          border-bottom: 1px solid ${COLORS.border};
          
          &:last-child {
            border-bottom: none;
          }
        }
      }
    }
  }
`;

const RpcEndpointDetails: React.FC = () => {
  const { rpcId } = useParams<{ rpcId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isMobile = useIsMobile();
  
  const [loading, setLoading] = useState(true);
  const [rpcData, setRpcData] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [pingStatus, setPingStatus] = useState<{ online: boolean; latency?: number; rateLimited?: boolean; detail?: string } | null>(null);
  const [blockchainType, setBlockchainType] = useState<string>('EVM');
  const [blockchains, setBlockchains] = useState<any[]>([]);
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    // Fetch blockchains for blockchainNetworkId dropdown
    const fetchBlockchains = async () => {
      try {
        const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS, {
          params: { pageId: 1, perPage: 100, status: 'ACTIVE' }
        });
        if (response.data?.status === 'success' && response.data?.data) {
          const data = response.data.data;
          const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
          setBlockchains(items.filter((b: any) => b.status === 'ACTIVE'));
        }
      } catch (error) {
        console.error('Error fetching blockchains:', error);
      }
    };
    fetchBlockchains();
  }, []);

  useEffect(() => {
    fetchRpcDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rpcId]);

  const fetchRpcDetails = async () => {
    if (!rpcId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/Administrator/settings/rpc/${rpcId}`);
      
      if (response.data.status === 'success') {
        setRpcData(response.data.data);
      } else {
        message.error('Failed to load RPC endpoint details');
      }
    } catch (error: any) {
      console.error('Error fetching RPC endpoint details:', error);
      message.error(error.response?.data?.message || 'Failed to load RPC endpoint details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (!rpcData) return;
    
    const currentBlockchainType = rpcData.blockchainType || 'EVM';
    setBlockchainType(currentBlockchainType);
    
    form.setFieldsValue({
      name: rpcData.name,
      providerName: rpcData.providerName || undefined,
      blockchainType: currentBlockchainType,
      blockchainNetworkId: rpcData.blockchainNetworkId,
      networkId: rpcData.networkId,
      rpcUrl: rpcData.rpcUrl,
      rpcUser: rpcData.rpcUser || undefined,
      rpcPassword: rpcData.rpcPassword ? '••••••••' : undefined, // Don't set actual password
      status: rpcData.status,
      isDefault: rpcData.isDefault,
      priority: rpcData.priority,
      description: rpcData.description,
      allowInOnchainWatcherMonitor: rpcData.allowInOnchainWatcherMonitor ?? true,
      supportsRPCPolling: rpcData.supportsRPCPolling ?? true,
      allowPendingTransaction: rpcData.allowPendingTransaction ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!rpcId) return;
      
      // If password is the masked value, don't send it (keep existing password)
      if (values.rpcPassword === '••••••••') {
        delete values.rpcPassword;
      }
      
      // Remove null/undefined values - Joi validation expects string or absent, not null
      const cleanedValues: Record<string, any> = {};
      for (const [key, value] of Object.entries(values)) {
        if (value !== null && value !== undefined && value !== '') {
          cleanedValues[key] = value;
        }
      }
      
      await apiClient.put((API_ENDPOINTS.ADMIN_UPDATE_RPC_ENDPOINT || '/api/Administrator/settings/rpc/update/{id}').replace('{id}', rpcId), cleanedValues);
      message.success('RPC endpoint updated successfully');
      setModalVisible(false);
      fetchRpcDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update RPC endpoint');
    }
  };

  const handlePing = async () => {
    if (!rpcId) return;
    
    try {
      setPinging(true);
      setPingStatus(null);
      const startTime = Date.now();
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_PING_RPC_ENDPOINT.replace('{id}', rpcId));
      const latency = Date.now() - startTime;
      
      if (response.data?.status === 'success' && response.data?.data) {
        setPingStatus({
          online: response.data.data.online || false,
          latency: response.data.data.latency || latency,
        });
        if (response.data.data.online) {
          message.success(`RPC endpoint is online (${response.data.data.latency || latency}ms)`);
        } else {
          message.error('RPC endpoint is offline or unreachable');
        }
      } else {
        message.error('Failed to ping RPC endpoint');
      }
    } catch (error: any) {
      // Rate-limited !== offline. A 429 proves the RPC answered us; only the
      // provider's throttle rejected this particular request. Surface it as
      // a distinct state so operators don't misdiagnose a healthy node.
      const status = error?.response?.status;
      const details = error?.response?.data?.details;
      const serverMessage: string = error?.response?.data?.message || '';
      const isRateLimited =
        status === 429 ||
        details?.rateLimited === true ||
        /rate[\s-]?limit|too many requests|throttl/i.test(serverMessage);

      if (isRateLimited) {
        setPingStatus({ online: false, rateLimited: true, detail: serverMessage || 'Rate limited' });
        message.warning(
          'RPC endpoint is rate limited (reachable, but throttled) — not offline'
        );
      } else {
        setPingStatus({ online: false });
        message.error(serverMessage || 'Failed to ping RPC endpoint');
      }
    } finally {
      setPinging(false);
    }
  };

  const handleResyncToWatcher = async () => {
    if (!rpcId) return;
    
    try {
      setResyncing(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_RESYNC_RPC_ENDPOINT_TO_WATCHER.replace('{id}', rpcId));
      
      if (response.data?.status === 'success') {
        message.success('RPC endpoint synced to OnchainWatcher successfully');
        fetchRpcDetails();
      } else {
        message.error(response.data?.message || 'Failed to sync to watcher');
      }
    } catch (error: any) {
      console.error('Error resyncing RPC endpoint to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync to watcher');
    } finally {
      setResyncing(false);
    }
  };

  const handleDelete = () => {
    if (!rpcData) return;

    confirm({
      title: 'Delete RPC Endpoint',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete "${rpcData.name}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      bodyStyle: { background: COLORS.bgSecondary, color: COLORS.textPrimary },
      onOk: async () => {
        try {
          await apiClient.post((API_ENDPOINTS.ADMIN_DELETE_RPC_ENDPOINT || '/api/Administrator/settings/rpc/delete/{id}').replace('{id}', rpcId!));
          message.success('RPC endpoint deleted successfully');
          navigate('/kivu/rpc-endpoints');
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to delete RPC endpoint');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!rpcData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            RPC Endpoint Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Space wrap style={{ width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kivu/rpc-endpoints')}>
            Back
          </Button>
          <Title 
            level={2} 
            style={{ 
              margin: 0, 
              color: COLORS.textPrimary,
              fontSize: isMobile ? '20px' : '24px'
            }}
          >
            RPC Endpoint Details
          </Title>
        </Space>
        <Space wrap style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handlePing}
            loading={pinging}
          >
            Ping
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResyncToWatcher}
            loading={resyncing}
            disabled={resyncing}
          >
            {isMobile ? 'Resync' : 'Resync to Watcher'}
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {rpcData.name}
              </div>
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="RPC Endpoint ID">{rpcData.id}</Descriptions.Item>
          <Descriptions.Item label="Watcher ID">
            {rpcData.watcherId ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{rpcData.watcherId}</span>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not synced</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Name">{rpcData.name}</Descriptions.Item>
          <Descriptions.Item label="Provider Name">{rpcData.providerName || <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not set</span>}</Descriptions.Item>
          <Descriptions.Item label="Blockchain Type">
            <Tag color="blue">{rpcData.blockchainType || 'EVM'}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Network ID">{rpcData.networkId ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="RPC URL">
            <span style={{ 
              wordBreak: 'break-all', 
              fontFamily: 'monospace',
              fontSize: isMobile ? '12px' : '14px'
            }}>
              {rpcData.rpcUrl || '-'}
            </span>
          </Descriptions.Item>
          {rpcData.blockchainType === 'BITCOIN' && (
            <>
              <Descriptions.Item label="RPC Username">{rpcData.rpcUser || '-'}</Descriptions.Item>
              <Descriptions.Item label="RPC Password">
                {rpcData.rpcPassword ? <Tag color="green">Password is set</Tag> : '-'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Priority">{rpcData.priority ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Description">{rpcData.description || '-'}</Descriptions.Item>
          <Descriptions.Item label="Is Default">
            <Tag color={rpcData.isDefault ? 'green' : 'default'}>
              {rpcData.isDefault ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(rpcData.status)}</Descriptions.Item>
          <Descriptions.Item label="AsoRock Acknowledged">
            <Tag color={rpcData.asorockAcknowledged ? 'green' : 'default'}>
              {rpcData.asorockAcknowledged ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Allow in OnchainWatcher Monitor">
            <Tag color={rpcData.allowInOnchainWatcherMonitor !== false ? 'green' : 'red'}>
              {rpcData.allowInOnchainWatcherMonitor !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Supports RPC Polling">
            <Tag color={rpcData.supportsRPCPolling !== false ? 'green' : 'red'}>
              {rpcData.supportsRPCPolling !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Allow Pending Transaction">
            <Tag color={rpcData.allowPendingTransaction !== false ? 'green' : 'orange'}>
              {rpcData.allowPendingTransaction !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Max Requests/Second">
            <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
              {rpcData.maxRequestsPerSecond ?? 250} RPS
            </span>
          </Descriptions.Item>
          {pingStatus && (
            <Descriptions.Item label="Ping Status">
              <Tag
                color={
                  pingStatus.online
                    ? 'success'
                    : pingStatus.rateLimited
                    ? 'warning'
                    : 'error'
                }
                title={pingStatus.detail}
              >
                {pingStatus.online
                  ? `Online (${pingStatus.latency}ms)`
                  : pingStatus.rateLimited
                  ? 'Rate limited'
                  : 'Offline'}
              </Tag>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Last Synced">
            {rpcData.lastSynced ? new Date(rpcData.lastSynced).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {rpcData.createdAt ? new Date(rpcData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {rpcData.updatedAt ? new Date(rpcData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <Modal
        title="Edit RPC Endpoint"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Update"
        cancelText="Cancel"
        width={isMobile ? '95%' : 600}
        bodyStyle={{ 
          background: COLORS.bgSecondary, 
          color: COLORS.textPrimary, 
          maxHeight: '70vh', 
          overflowY: 'auto',
          padding: isMobile ? '16px' : '24px'
        }}
        style={{ top: isMobile ? 10 : 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., INFURA_MAINNET" />
          </Form.Item>
          <Form.Item
            name="providerName"
            label="Provider Name"
          >
            <Input placeholder="e.g., Infura, Alchemy, QuickNode" />
          </Form.Item>
          <Form.Item
            name="blockchainType"
            label="Blockchain Type"
            rules={[{ required: true, message: 'Please select blockchain type' }]}
          >
            <Select onChange={(value) => setBlockchainType(value)}>
              <Select.Option value="EVM">EVM</Select.Option>
              <Select.Option value="BITCOIN">Bitcoin</Select.Option>
              <Select.Option value="SOLANA">Solana</Select.Option>
              <Select.Option value="TRON">TRON</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="blockchainNetworkId"
            label="Blockchain Network"
            tooltip="Select the blockchain network this RPC endpoint belongs to"
          >
            <Select
              placeholder="Select blockchain network (optional)"
              showSearch
              filterOption={(input, option: any) => {
                if (!option) return false;
                const children = option.children;
                if (!children) return false;
                const childrenStr = Array.isArray(children) 
                  ? children.join('') 
                  : String(children);
                return childrenStr.toLowerCase().includes(input.toLowerCase());
              }}
              allowClear
            >
              {blockchains
                .filter((b: any) => b.blockchainType === blockchainType || (!b.blockchainType && blockchainType === 'EVM'))
                .map((blockchain: any) => (
                  <Select.Option key={blockchain.id} value={blockchain.id}>
                    {blockchain.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="networkId"
            label="Network ID"
            rules={[{ required: true, message: 'Please enter network ID' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g., 1" />
          </Form.Item>
          <Form.Item
            name="rpcUrl"
            label="RPC URL"
            rules={[{ required: true, message: 'Please enter RPC URL' }]}
          >
            <Input
              placeholder="https://..."
              onChange={(e) => {
                const url = e.target.value.toLowerCase();
                if (url.includes('bitcoin') || url.includes('btc')) {
                  form.setFieldsValue({ blockchainType: 'BITCOIN' });
                  setBlockchainType('BITCOIN');
                } else if (url.includes('solana') || url.includes('sol')) {
                  form.setFieldsValue({ blockchainType: 'SOLANA' });
                  setBlockchainType('SOLANA');
                }
              }}
            />
          </Form.Item>
          <div style={{ color: '#ff4d4f', fontSize: '14px', marginBottom: '16px', padding: '8px', background: 'rgba(255, 77, 79, 0.1)', borderRadius: '4px', border: '1px solid rgba(255, 77, 79, 0.2)' }}>
            ⚠️ Please do not add Public RPC endpoints
          </div>
          {blockchainType === 'BITCOIN' && (
            <>
              <Form.Item
                name="rpcUser"
                label="RPC Username"
                tooltip="Optional: Required only for private Bitcoin RPC endpoints. Public RPCs (e.g., bitcoin-rpc.publicnode.com) don't require authentication."
              >
                <Input placeholder="Bitcoin Core RPC username (optional for public RPCs)" />
              </Form.Item>
              <Form.Item
                name="rpcPassword"
                label="RPC Password"
                tooltip="Optional: Required only if username is provided. Leave empty for public RPC endpoints."
                extra={rpcData?.rpcPassword ? 'Leave empty to keep existing password, or enter new password to change it' : 'Optional: Only required for private Bitcoin RPC endpoints'}
              >
                <Input.Password autoComplete="new-password" placeholder={rpcData?.rpcPassword ? 'Enter new password or leave empty' : 'Bitcoin Core RPC password (optional for public RPCs)'} />
              </Form.Item>
            </>
          )}
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please enter priority' }]}
            extra="1 = LIVE, 2 = CATCHUP"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
          </Form.Item>
          <Form.Item
            name="maxRequestsPerSecond"
            label="Max Requests Per Second"
            tooltip="Maximum number of requests per second allowed by this RPC provider. Default: 250. Used to prevent hitting rate limits."
            initialValue={250}
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} placeholder="250" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            name="isDefault"
            label="Is Default"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="DELETED">Deleted</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="allowPendingTransaction"
            label="Allow Pending Transaction"
            valuePropName="checked"
            initialValue={true}
            tooltip="Whether this RPC endpoint allows pending transaction queries. When disabled, the system will not attempt to query pending transactions from this endpoint."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default RpcEndpointDetails;
